import { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { ALL_NAV_ITEMS, type NavItemDef } from '../../constants/navItems';
import { useI18n } from '../../hooks/useI18n';

interface MenuRow {
  id:         string;
  sort_order: number;
  enabled:    boolean;
}

interface MergedItem extends NavItemDef {
  sort_order: number;
  enabled:    boolean;
}

export function AdminMenuPage() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const { t } = useI18n();
  const [rows,    setRows]    = useState<MenuRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('nav_menu_config')
      .select('id, sort_order, enabled')
      .order('sort_order');
    if (data) setRows(data);
    setLoading(false);
  }

  function getItems(): MergedItem[] {
    return [...ALL_NAV_ITEMS]
      .map(item => {
        const row = rows.find(r => r.id === item.id);
        return {
          ...item,
          sort_order: row?.sort_order ?? item.defaultOrder,
          enabled:    row?.enabled    ?? true,
        };
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  async function saveRows(updated: MenuRow[]) {
    setSaving(true);
    const { error } = await supabase
      .from('nav_menu_config')
      .upsert(
        updated.map(r => ({ ...r, updated_at: new Date().toISOString(), updated_by: profile?.id })),
        { onConflict: 'id' }
      );
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar configuração do menu' });
    } else {
      setRows(updated);
      showToast({ type: 'success', message: 'Menu atualizado' });
    }
    setSaving(false);
  }

  function move(id: string, dir: 1 | -1) {
    const items = getItems();
    const idx = items.findIndex(i => i.id === id);
    if (idx < 0) return;
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= items.length) return;

    const newRows: MenuRow[] = items.map((item, i) => {
      let order = item.sort_order;
      if (i === idx)     order = items[swapIdx].sort_order;
      if (i === swapIdx) order = items[idx].sort_order;
      return { id: item.id, sort_order: order, enabled: item.enabled };
    });
    saveRows(newRows);
  }

  function toggleEnabled(id: string) {
    const items = getItems();
    const newRows: MenuRow[] = items.map(item => ({
      id:         item.id,
      sort_order: item.sort_order,
      enabled:    item.id === id ? !item.enabled : item.enabled,
    }));
    saveRows(newRows);
  }

  const items = getItems();

  return (
    <div>
      <AdminPageHeader
        title="Menu de Navegação"
        objective="Controle a ordem e visibilidade dos itens do menu lateral. Itens desabilitados aparecem no menu acinzentados com ícone 'em breve', mas não são clicáveis. As alterações têm efeito imediato para todos os usuários."
      />

      {loading ? (
        <div className="card shimmer" style={{ height: '400px' }} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>Item</th>
                <th>Rota</th>
                <th style={{ textAlign: 'center' }}>Visível</th>
                <th style={{ textAlign: 'center' }}>Ordem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const label = item.label.startsWith('nav.')
                  ? t(item.label as Parameters<typeof t>[0])
                  : item.label;

                return (
                  <tr key={item.id} style={{ opacity: item.enabled ? 1 : 0.5 }}>
                    <td style={{ color: 'var(--text-muted)', fontSize: '12px', fontFamily: 'monospace' }}>
                      {item.sort_order}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1rem', fontFamily: 'var(--font-display)', color: 'var(--celeste-soft)', width: '1.2rem', textAlign: 'center' }}>
                          {item.glyph ?? '·'}
                        </span>
                        <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{label}</span>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {item.to}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className={item.enabled ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                        onClick={() => toggleEnabled(item.id)}
                        disabled={saving}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                        title={item.enabled ? 'Desabilitar item' : 'Habilitar item'}
                      >
                        {item.enabled ? <Eye size={13} /> : <EyeOff size={13} />}
                        {item.enabled ? 'Visível' : 'Oculto'}
                      </button>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => move(item.id, -1)}
                          disabled={saving || idx === 0}
                          style={{ padding: '3px 7px' }}
                          title="Mover para cima"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => move(item.id, 1)}
                          disabled={saving || idx === items.length - 1}
                          style={{ padding: '3px 7px' }}
                          title="Mover para baixo"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
